import type { Cam } from './puzzles/cameras.ts';
import { covers, DIRS8 } from './puzzles/cameras.ts';
import type { PasswordClue } from './puzzles/password.ts';
import type { Rule, WireColor } from './puzzles/panel.ts';
import type { Constraint } from './puzzles/safe.ts';
import type { Direction4, PhraseRef } from './types.ts';
import { PASSWORD_CATEGORIES } from './phrases.ts';

/**
 * How a clue becomes quick phrases and back (GDD §13.1). The vocabulary is in `phrases.ts`;
 * this file fixes what the numbers mean: positions, pins and cameras are 1-based on the wire,
 * letters are A=0, digits and symbols are raw. Bots and the client's "say it" buttons share it,
 * so a human and a bot always understand each other.
 */

export type PhraseParam = number | string;

export const ref = (id: string, ...params: PhraseParam[]): PhraseRef => ({ id, params });

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
export const letterIndex = (ch: string): number => Math.max(0, LETTERS.indexOf(ch.toUpperCase()));
export const letterAt = (i: number): string => LETTERS.charAt(i);

const num = (p: PhraseParam | undefined): number => (typeof p === 'number' ? p : 0);
const str = (p: PhraseParam | undefined): string => (typeof p === 'string' ? p : '');

// ---- Lock ------------------------------------------------------------------------------

export const lockNextPinPhrase = (pin: number): PhraseRef => ref('lockNextPin', pin + 1);
export const lockHeightPhrase = (pin: number, height: number): PhraseRef =>
  ref('lockPinHeight', pin + 1, height);

// ---- Safe ------------------------------------------------------------------------------

export function constraintToPhrase(k: Constraint): PhraseRef {
  switch (k.k) {
    case 'digitAt':
      return ref('safeDigit', k.pos + 1, k.d);
    case 'contains':
      return ref('safeClue', k.d);
    case 'notContains':
      return ref('safeNo', k.d);
    case 'sum':
      return ref('safeSum', Math.min(45, k.s));
    case 'parity':
      return ref('safeParity', k.pos + 1, k.even ? 'even' : 'odd');
    case 'greater':
      return ref('safeGreater', k.a + 1, k.b + 1);
  }
}

export function phraseToConstraint(p: PhraseRef): Constraint | null {
  const a = p.params;
  switch (p.id) {
    case 'safeDigit':
      return { k: 'digitAt', pos: num(a[0]) - 1, d: num(a[1]) };
    case 'safeClue':
      return { k: 'contains', d: num(a[0]) };
    case 'safeNo':
      return { k: 'notContains', d: num(a[0]) };
    case 'safeSum':
      return { k: 'sum', s: num(a[0]) };
    case 'safeParity':
      return { k: 'parity', pos: num(a[0]) - 1, even: str(a[1]) === 'even' };
    case 'safeGreater':
      return { k: 'greater', a: num(a[0]) - 1, b: num(a[1]) - 1 };
    default:
      return null;
  }
}

// ---- Panel -----------------------------------------------------------------------------

export function panelRuleToPhrase(n: number, r: Rule): PhraseRef {
  const c = r.cond;
  switch (c.k) {
    case 'color':
      return ref('panelRule', n, 'color', c.color, r.act);
    case 'colorSerialOdd':
      return ref('panelRule', n, 'odd', c.color, r.act);
    case 'colorSerialEven':
      return ref('panelRule', n, 'even', c.color, r.act);
    case 'position':
      return ref('panelRule', n, c.where, 'any', r.act);
    case 'colorCountAtLeast':
      return ref('panelRule', n, 'twoPlus', c.color, r.act);
    case 'serialHasLetter':
      return ref('panelRuleLetter', n, c.color, letterIndex(c.letter), r.act);
  }
}

export function phraseToPanelRule(p: PhraseRef): { n: number; rule: Rule } | null {
  const a = p.params;
  const n = num(a[0]);
  if (p.id === 'panelRuleLetter') {
    return {
      n,
      rule: {
        cond: { k: 'serialHasLetter', color: str(a[1]) as WireColor, letter: letterAt(num(a[2])) },
        act: str(a[3]) as Rule['act'],
      },
    };
  }
  if (p.id !== 'panelRule') return null;
  const color = str(a[2]) as WireColor;
  const act = str(a[3]) as Rule['act'];
  switch (str(a[1])) {
    case 'color':
      return { n, rule: { cond: { k: 'color', color }, act } };
    case 'odd':
      return { n, rule: { cond: { k: 'colorSerialOdd', color }, act } };
    case 'even':
      return { n, rule: { cond: { k: 'colorSerialEven', color }, act } };
    case 'first':
      return { n, rule: { cond: { k: 'position', where: 'first' }, act } };
    case 'last':
      return { n, rule: { cond: { k: 'position', where: 'last' }, act } };
    case 'twoPlus':
      return { n, rule: { cond: { k: 'colorCountAtLeast', color, n: 2 }, act } };
    default:
      return null;
  }
}

// ---- Password --------------------------------------------------------------------------

export function passwordClueToPhrase(c: PasswordClue): PhraseRef {
  if (c.k === 'category') return ref('passClue', 'category', PASSWORD_CATEGORIES.indexOf(c.cat));
  return ref('passClue', c.k, letterIndex(c.letter));
}

export function phraseToPasswordClue(p: PhraseRef): PasswordClue | null {
  if (p.id !== 'passClue') return null;
  const kind = str(p.params[0]);
  const idx = num(p.params[1]);
  if (kind === 'category') {
    const cat = PASSWORD_CATEGORIES[idx];
    return cat ? { k: 'category', cat } : null;
  }
  if (kind === 'first' || kind === 'last' || kind === 'contains' || kind === 'excludes') {
    return { k: kind, letter: letterAt(idx) };
  }
  return null;
}

// ---- Cameras ---------------------------------------------------------------------------

/** For every camera, one phrase per direction that would see a cell of the guards' path. */
export function cameraAvoidPhrases(
  cams: readonly Cam[],
  path: readonly { x: number; y: number }[],
): { camera: number; dir: string; phrase: PhraseRef }[] {
  const out: { camera: number; dir: string; phrase: PhraseRef }[] = [];
  for (const cam of cams) {
    for (const dir of DIRS8) {
      if (path.some((cell) => covers(cam, dir, cell))) {
        out.push({ camera: cam.id, dir, phrase: ref('camAvoid', cam.id + 1, dir) });
      }
    }
  }
  return out;
}

// ---- Corridor and vault ----------------------------------------------------------------

export const guardPhrase = (guard: number, dir: Direction4): PhraseRef =>
  ref('corGuard', guard + 1, dir);
export const vaultPosPhrase = (pos: number, symbol: number): PhraseRef =>
  ref('vaultPos', pos + 1, symbol);
